import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { HbsError, startTrial } from '../api/hbs';
import { ApiError, bootstrapOnboarding } from '../api/client';
import { pesanGagalSignup, sebabGagalSignup } from '../lib/galat-signup';
import { slugify } from '../lib/slugify';
import { supabase } from '../lib/supabase';
import { IsianSandi } from '../components/IsianSandi';

/**
 * Trial 14 hari Workforce — rute PUBLIK `/trial` (BLD-015 lanjutan, 2 Okt 2026, keputusan Owner
 * langsung: "buat masing-masing tombol trial, workforce sendiri, CRM sendiri jangan digabung").
 *
 * BUKAN Demo Instan (dihapus bersamaan, lihat PR yang sama) — ini organisasi `customer`
 * SUNGGUHAN dengan password sungguhan yang dipilih sendiri & trial 14 hari sungguhan
 * (`LanggananService.mulaiTrial`, ADR-0045 §3C D2, endpoint yang SUDAH berjalan untuk pendaftar
 * mandiri CRM sejak 13 Sep 2026 — halaman ini cuma pemanggil baru darinya, nol endpoint baru).
 * Kosong (tanpa data contoh) karena memang untuk data bisnis sungguhan pelanggan sendiri.
 *
 * TIGA LANGKAH, mirip 2 halaman terpisah CRM (`LoginPage` mode daftar + `OnboardingPage`)
 * digabung satu halaman: (1) email+kata sandi → Supabase `signUp`, (2) tautan konfirmasi email
 * dibuka → mendarat KEMBALI di halaman ini dengan sesi aktif (dideteksi otomatis saat mount,
 * `emailRedirectTo` menunjuk balik ke sini) → (3) nama organisasi → `POST /onboarding`.
 *
 * Berdiri DI LUAR batas autentikasi (pola sama `AcceptInvitePage.tsx`/`/accept`) — pendaftar
 * belum punya apa pun untuk masuk dengannya di langkah 1.
 */
export function TrialStartPage(): ReactNode {
  const [step, setStep] = useState<'signup' | 'checkEmail' | 'org'>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [orgName, setOrgName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mendarat dari tautan konfirmasi email — sesi Supabase sudah terbentuk sebelum komponen ini
  // mount (klien memproses URL saat inisialisasi). Cukup sekali saat mount, bukan langganan
  // perubahan: ini pemulihan kedatangan, bukan reaksi berkelanjutan terhadap status auth.
  useEffect(() => {
    let aktif = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (aktif && data.session) setStep('org');
    });
    return () => {
      aktif = false;
    };
  }, []);

  async function onSignUp(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const { data, error: gagal } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: { emailRedirectTo: `${window.location.origin}/trial` },
    });
    setSubmitting(false);
    if (gagal) {
      setError(pesanGagalSignup(sebabGagalSignup(gagal)));
      return;
    }
    // Sesi langsung ada (mis. konfirmasi email dimatikan di proyek Supabase) — langsung lanjut,
    // tidak perlu menunggu klik apa pun.
    if (data.session) {
      setStep('org');
      return;
    }
    setStep('checkEmail');
  }

  async function onCreateOrg(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const { data } = await supabase.auth.getSession();
    const accessToken = data.session?.access_token;
    if (!accessToken) {
      setSubmitting(false);
      setError('Sesi berakhir. Muat ulang halaman dan coba lagi.');
      return;
    }
    const effectiveSlug = slugTouched ? slug : slugify(orgName);
    try {
      const { organizationId } = await startTrial({
        organizationName: orgName.trim(),
        slug: effectiveSlug,
        accessToken,
      });
      /*
       * QA-WF-003/BLD-017 — `startTrial` (HBS `/onboarding`) membuat organisasi di CRM dan
       * menjadikan pemanggil Owner DI SANA, tapi tidak pernah menyentuh peran Workforce
       * (sistem yang benar-benar terpisah, ADR-0033). Tanpa panggilan ini, pemilik organisasi
       * baru mendarat di Workforce TANPA peran — "Belum punya peran Workforce", jalan buntu
       * tanpa admin lain yang bisa dihubungi, karena dialah satu-satunya orang di organisasi
       * itu. Ditemukan roleplay pendaftaran mandiri 4 Okt 2026 (QA-WF-003).
       */
      await bootstrapOnboarding({ accessToken, organizationId });
      // Sesi baru terbentuk lengkap dengan organisasi & peran — App.tsx membaca ulang & membawa
      // masuk, pola sama DemoStartPage (sekarang dihapus) & AcceptInvitePage: navigasi keras,
      // bukan useNavigate(), karena layar ini dirender sebelum BrowserRouter dipasang di App.tsx.
      window.location.assign('/');
    } catch (err) {
      setSubmitting(false);
      setError(
        err instanceof HbsError || err instanceof ApiError
          ? err.message
          : 'Gagal membuat organisasi. Coba lagi sesaat lagi.',
      );
    }
  }

  if (step === 'checkEmail') {
    return (
      <div className="card">
        <h2>Periksa email Anda</h2>
        <p className="muted small">
          Kami mengirim tautan konfirmasi ke <strong>{email.trim()}</strong>. Klik tautan itu untuk
          melanjutkan — Anda akan kembali ke sini secara otomatis.
        </p>
        {error ? <p role="alert">{error}</p> : null}
      </div>
    );
  }

  if (step === 'org') {
    const effectiveSlug = slugTouched ? slug : slugify(orgName);
    return (
      <form className="card" onSubmit={(e) => void onCreateOrg(e)}>
        <h2>Buat organisasi Anda</h2>
        <p className="muted small">Langkah terakhir — trial 14 hari dimulai begitu ini dibuat.</p>
        {error ? <p role="alert">{error}</p> : null}
        <label htmlFor="orgName">Nama organisasi</label>
        <input
          id="orgName"
          required
          value={orgName}
          onChange={(e) => setOrgName(e.target.value)}
          placeholder="mis. PT Sejahtera Medika"
        />
        <label htmlFor="slug">Alamat (unik)</label>
        <input
          id="slug"
          required
          value={effectiveSlug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(slugify(e.target.value));
          }}
        />
        <p className="muted small">huruf kecil, angka, tanda hubung</p>
        <button
          type="submit"
          className="button-primary"
          disabled={submitting || !orgName.trim() || !effectiveSlug}
        >
          {submitting ? 'Membuat…' : 'Mulai Trial 14 Hari'}
        </button>
      </form>
    );
  }

  return (
    <form className="card" onSubmit={(e) => void onSignUp(e)}>
      <h2>Mulai Trial 14 Hari</h2>
      <p className="muted small">
        Buat akun Anda sendiri — tanpa kartu kredit, langsung bisa dipakai untuk data bisnis Anda
        sendiri.
      </p>
      {error ? <p role="alert">{error}</p> : null}
      <label htmlFor="email">Email</label>
      <input
        id="email"
        type="email"
        autoComplete="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <label htmlFor="password">Kata sandi</label>
      <IsianSandi
        id="password"
        autoComplete="new-password"
        required
        minLength={6}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <button type="submit" className="button-primary" disabled={submitting}>
        {submitting ? 'Mendaftar…' : 'Lanjut'}
      </button>
    </form>
  );
}
